import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Upload, FileSpreadsheet, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "lead_name", label: "Lead Name *" },
  { key: "business_name", label: "Business Name" },
  { key: "contact_person", label: "Contact Person" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "website", label: "Website" },
  { key: "industry", label: "Industry" },
  { key: "location", label: "Location" },
  { key: "source", label: "Source" },
  { key: "temperature", label: "Temperature" },
];

export default function ImportLeads() {
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [skipDup, setSkipDup] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/leads/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      // auto-map by name match
      const m = {};
      FIELDS.forEach((f) => {
        const found = data.columns.find((c) => c.toLowerCase().replace(/[_\s-]/g, "") === f.key.replace(/_/g, ""));
        if (found) m[f.key] = found;
      });
      setMapping(m);
      setStep(2);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const commit = async () => {
    if (!mapping.lead_name && !mapping.business_name) {
      toast.error("Map at least Lead Name or Business Name"); return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/leads/import/commit", { mapping, data: preview.all_rows || preview.rows, skip_duplicates: skipDup });
      setResult(data); setStep(3);
    } catch (e) { toast.error(formatApiError(e)); } finally { setImporting(false); }
  };

  return (
    <div className="p-8" data-testid="import-page">
      <div className="eyb-label">Bulk Operations</div>
      <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Import Leads</h1>
      <p className="text-sm text-slate-500 mt-1 mb-8">Upload a CSV or Excel file. The system will parse, preview and let you map columns before creating leads.</p>

      <Stepper step={step} />

      {step === 1 && (
        <div className="eyb-card p-10 text-center mt-6">
          <FileSpreadsheet className="w-12 h-12 text-[#29ABE2] mx-auto mb-3" />
          <h3 className="font-heading font-semibold text-slate-900">Upload a file</h3>
          <p className="text-sm text-slate-500 mt-1 mb-6">.csv, .xlsx — max 10MB recommended</p>
          <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold cursor-pointer">
            <Upload className="w-4 h-4" /> Choose file
            <input data-testid="file-input" type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" />
          </label>
        </div>
      )}

      {step === 2 && preview && (
        <div className="space-y-6 mt-6">
          <div className="eyb-card p-6">
            <h3 className="font-heading font-semibold text-slate-900 mb-1">Map columns ({preview.total_rows} rows detected)</h3>
            <p className="text-xs text-slate-500 mb-4">Match each lead field with a column from your file.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-3">
                  <span className="text-sm font-medium w-40 text-slate-700">{f.label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <select value={mapping[f.key] || ""} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })} className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                    <option value="">— skip —</option>
                    {preview.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 mt-6 text-sm">
              <input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} />
              Skip duplicates (by email, phone, or business name)
            </label>
          </div>

          <div className="eyb-card p-6">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">Preview (first {Math.min(20, preview.rows.length)} rows)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-100">{preview.columns.map((c) => <th key={c} className="py-2 px-2">{c}</th>)}</tr></thead>
                <tbody>
                  {preview.rows.slice(0, 10).map((r, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      {preview.columns.map((c) => <td key={c} className="py-2 px-2 text-slate-700">{r[c]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => { setStep(1); setPreview(null); }} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="import-commit-btn" disabled={importing} onClick={commit} className="px-5 py-2 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold">{importing ? "Importing…" : "Import leads"}</button>
          </div>
        </div>
      )}

      {step === 3 && result && (
        <div className="eyb-card p-10 text-center mt-6">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h3 className="font-heading font-semibold text-slate-900 text-xl">Import complete</h3>
          <p className="text-sm text-slate-600 mt-2">Created <span className="font-bold text-emerald-600">{result.created}</span> leads · Skipped <span className="font-bold text-amber-600">{result.skipped}</span> (duplicates or invalid)</p>
          <button onClick={() => { setStep(1); setPreview(null); setResult(null); }} className="mt-6 px-5 py-2 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold">Import another file</button>
        </div>
      )}
    </div>
  );
}

function Stepper({ step }) {
  const steps = ["Upload", "Map & Preview", "Complete"];
  return (
    <div className="flex items-center gap-3">
      {steps.map((s, i) => {
        const n = i + 1;
        return (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= n ? "bg-[#29ABE2] text-white" : "bg-slate-200 text-slate-500"}`}>{n}</div>
            <span className={`text-sm font-medium ${step >= n ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
            {n < steps.length && <div className="w-12 h-px bg-slate-200" />}
          </div>
        );
      })}
    </div>
  );
}
